import { apiClient } from './apiClient';
import { mockCurrentUser } from '@/mocks/data';
import type { User } from '@/types';

export interface LoginRequest {
  username: string;
  password: string;
  useActiveDirectory?: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresAt: string;
}

// Flag để bật/tắt mock khi chưa có backend
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_BASE_URL;

export const authService = {
  async login(payload: LoginRequest): Promise<LoginResponse> {
    if (USE_MOCK) {
      // Mock: chấp nhận mọi user/pass, trả về current user
      await new Promise((resolve) => setTimeout(resolve, 600));
      if (payload.password.length < 3) {
        throw new Error('Sai thông tin đăng nhập');
      }
      return {
        token: 'mock-jwt-token-' + Date.now(),
        user: mockCurrentUser,
        expiresAt: new Date(Date.now() + 8 * 3600 * 1000).toISOString(),
      };
    }
    const { data } = await apiClient.post<LoginResponse>('/auth/login', payload);
    return data;
  },

  async logout(): Promise<void> {
    if (USE_MOCK) return;
    await apiClient.post('/auth/logout');
  },

  async me(): Promise<User> {
    if (USE_MOCK) return mockCurrentUser;
    const { data } = await apiClient.get<User>('/auth/me');
    return data;
  },

  async requestOtp(purpose: 'SIGN' | 'RESET'): Promise<{ expiresIn: number }> {
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 400));
      return { expiresIn: 300 };
    }
    const { data } = await apiClient.post<{ expiresIn: number }>('/auth/otp/request', { purpose });
    return data;
  },

  async verifyOtp(code: string): Promise<{ valid: boolean }> {
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 500));
      return { valid: code === '837591' || code.length === 6 };
    }
    const { data } = await apiClient.post<{ valid: boolean }>('/auth/otp/verify', { code });
    return data;
  },

  async updateProfile(payload: {
    fullName?: string;
    email?: string;
    phone?: string;
    position?: string;
    gender?: 'UNKNOWN' | 'MALE' | 'FEMALE';  // V1.0.48
  }): Promise<User> {
    // Strip empty strings và undefined
    const clean: any = {};
    for (const [k, v] of Object.entries(payload)) {
      if (v !== undefined && v !== '') clean[k] = v;
    }
    const { data } = await apiClient.patch<User>('/auth/me', clean);
    return data;
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', { oldPassword, newPassword });
  },

  async uploadSignature(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<User>('/auth/signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  getSignatureUrl(userId: string): string {
    // Trả URL để <img src> có thể render trực tiếp (endpoint có auth nhưng img tag không gửi Bearer token)
    // → dùng base URL + user ID, caller nên convert sang blob nếu cần
    const base = import.meta.env.VITE_API_BASE_URL || '';
    return `${base}/auth/signature/${userId}`;
  },
};
